import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { COUNTRIES, getCountryCode, localized, selectCountry } from '../../services/country';
import styles from './Navbar.module.css';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get('/notifications').then(r => setUnread(r.data.unread)).catch(()=>{});
    const t = setInterval(() => api.get('/notifications').then(r => setUnread(r.data.unread)).catch(()=>{}), 60000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => { logout(); navigate('/'); setMenuOpen(false); };

  const avatar = user?.profile?.avatar_url
    ? <img src={user.profile.avatar_url} alt="avatar" className={styles.avatarImg} />
    : <span className={styles.avatarInitial}>{(user?.profile?.name || user?.email || 'U')[0].toUpperCase()}</span>;

  return (
    <nav className={styles.navbar} aria-label={localized('Navegación principal','Navegação principal')}>
      <div className={styles.inner}>
        <div className={styles.left}>
          <Link to="/" className={styles.brand} onClick={()=>setMobileOpen(false)} aria-label="TalentosRD, inicio">
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="var(--primary)"/>
              <path d="M8 22L16 10L24 22H8Z" fill="white" fillOpacity="0.9"/>
              <circle cx="16" cy="10" r="3" fill="var(--accent)"/>
            </svg>
            <span>TalentosRD</span>
          </Link>
          <div className={`${styles.links} ${mobileOpen ? styles.mobileOpen : ''}`}>
            <NavLink to="/jobs" className={({isActive})=>isActive?styles.activeLink:''} onClick={()=>setMobileOpen(false)}>{localized('Empleos','Vagas')}</NavLink>
            <NavLink to="/jobs/map" className={({isActive})=>isActive?styles.activeLink:''} onClick={()=>setMobileOpen(false)}>Mapa</NavLink>
            <NavLink to="/companies" className={({isActive})=>isActive?styles.activeLink:''} onClick={()=>setMobileOpen(false)}>Empresas</NavLink>
            {user?.role === 'company' && (
              <NavLink to="/dashboard/company" className={({isActive})=>isActive?styles.activeLink:''} onClick={()=>setMobileOpen(false)}>{localized('Mi Empresa','Minha empresa')}</NavLink>
            )}
            {user?.role === 'candidate' && (
              <NavLink to="/dashboard/candidate" className={({isActive})=>isActive?styles.activeLink:''} onClick={()=>setMobileOpen(false)}>{localized('Mi Perfil','Meu perfil')}</NavLink>
            )}
          </div>
        </div>

        <div className={styles.right}>
          <label className={styles.countryPicker} title={localized('Seleccionar mercado','Selecionar mercado')}>
            <span className={styles.countryLabel}>{localized('País','País')}</span>
            <select value={getCountryCode()} onChange={event=>selectCountry(event.target.value)} aria-label={localized('Seleccionar país','Selecionar país')}>
              {Object.values(COUNTRIES).map(country=><option key={country.code} value={country.code} disabled={!country.active}>
                {country.flag} {country.name}{country.active?'':' · Próximamente'}
              </option>)}
            </select>
          </label>
          {!user ? (
            <>
              <Link to="/login" className={`btn btn-ghost btn-sm hide-mobile`}>{localized('Iniciar sesión','Entrar')}</Link>
              <Link to="/register" className={`btn btn-primary btn-sm`}>{localized('Registrarse','Cadastrar')}</Link>
            </>
          ) : (
            <>
              {user.role === 'company' && (
                <Link to="/jobs/new" className={`btn btn-primary btn-sm hide-mobile`}>
                  + {localized('Publicar empleo','Publicar vaga')}
                </Link>
              )}
              <Link to="/notifications" className={styles.notifBtn}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
                </svg>
                {unread > 0 && <span className={styles.badge}>{unread > 9 ? '9+' : unread}</span>}
              </Link>
              <div className={styles.profileMenu} ref={menuRef}>
                <button className={styles.avatarBtn} onClick={() => setMenuOpen(o=>!o)}>
                  <div className={styles.avatar}>{avatar}</div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                {menuOpen && (
                  <div className={styles.dropdown}>
                    <div className={styles.dropdownHeader}>
                      <strong>{user?.profile?.name || user?.email}</strong>
                      <span>{user?.role === 'admin' ? 'Administrador' : user?.role === 'company' ? 'Empresa' : 'Candidato'}</span>
                    </div>
                    <div className={styles.dropdownItems}>
                      {user.role === 'candidate' && <>
                        <Link to="/dashboard/candidate" onClick={()=>setMenuOpen(false)}>Mi perfil</Link>
                        <Link to="/dashboard/candidate/applications" onClick={()=>setMenuOpen(false)}>Mis postulaciones</Link>
                        <Link to="/dashboard/candidate/saved" onClick={()=>setMenuOpen(false)}>Empleos guardados</Link>
                      </>}
                      {user.role === 'company' && <>
                        <Link to="/dashboard/company" onClick={()=>setMenuOpen(false)}>Dashboard</Link>
                        <Link to="/dashboard/company/jobs" onClick={()=>setMenuOpen(false)}>Mis ofertas</Link>
                        <Link to="/jobs/new" onClick={()=>setMenuOpen(false)}>Publicar empleo</Link>
                      </>}
                      {user.role === 'admin' && <Link to="/dashboard/admin" onClick={()=>setMenuOpen(false)}>Panel administrativo</Link>}
                      {user.role !== 'admin' && <Link to={`/dashboard/${user.role}/settings`} onClick={()=>setMenuOpen(false)}>Configuración</Link>}
                      <hr/>
                      <button onClick={handleLogout} className={styles.logoutBtn}>Cerrar sesión</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          <button className={`${styles.hamburger} ${mobileOpen ? styles.hamburgerOpen : ''}`} onClick={()=>setMobileOpen(o=>!o)} aria-expanded={mobileOpen} aria-label={mobileOpen ? localized('Cerrar menú','Fechar menu') : localized('Abrir menú','Abrir menu')}>
            <span/><span/><span/>
          </button>
        </div>
      </div>
    </nav>
  );
}
